import type { CaseEntryType } from '../types/caseEntry'
import type { QuoteLine } from '../types/quote'
import { formatKr, lineSubtotal } from './quote'

/**
 * Tilbuddet er estimatet/aftalen; sagen er det faktiske forbrug.
 * Tilbudslinjer har ingen type, så typen gættes ud fra enhed og beskrivelse.
 */
export function guessEntryType(line: QuoteLine): CaseEntryType {
  const text = (line.description || '').toLowerCase()
  const unit = line.unit as string
  if (unit === 'timer') return 'Timer'
  if (unit === 'km' || /kørsel|kilometer|transport/.test(text)) return 'Kørsel'
  if (/servicevogn|værktøj/.test(text)) return 'Servicevogn og værktøj'
  if (/maskin|leje|vibrator|minigraver|gravemaskine|brænder|fræser|kompressor/.test(text)) return 'Maskiner'
  if (/bortskaf|affald|genbrugs|container|deponi|tipgebyr/.test(text)) return 'Bortskaffelse'
  if (unit === 'fast pris') return 'Andet'
  return 'Materialer'
}

/** De fire grupper på "Forbrug på sagen" — estimat og forbrug grupperes ens. */
export type ConsumptionGroup = 'Timer' | 'Materialer' | 'Kørsel & service' | 'Øvrigt'

export const consumptionGroups: ConsumptionGroup[] = ['Timer', 'Materialer', 'Kørsel & service', 'Øvrigt']

export const groupTypes: Record<ConsumptionGroup, CaseEntryType[]> = {
  Timer: ['Timer'],
  Materialer: ['Materialer'],
  'Kørsel & service': ['Kørsel', 'Servicevogn og værktøj'],
  Øvrigt: ['Maskiner', 'Bortskaffelse', 'Andet'],
}

export function groupOfType(type: CaseEntryType): ConsumptionGroup {
  return consumptionGroups.find((group) => groupTypes[group].includes(type)) ?? 'Øvrigt'
}

export function groupOfLine(line: QuoteLine): ConsumptionGroup {
  return groupOfType(guessEntryType(line))
}

export interface EstimateTotals {
  hours: number
  hoursCount: number
  materials: number
  travelService: number
  other: number
  subtotal: number
  vatAmount: number
  total: number
}

export function estimateTotals(lines: QuoteLine[]): EstimateTotals {
  const sumOf = (group: ConsumptionGroup) =>
    lines.filter((line) => groupOfLine(line) === group).reduce((sum, line) => sum + lineSubtotal(line), 0)
  const subtotal = lines.reduce((sum, line) => sum + lineSubtotal(line), 0)
  const vatAmount = lines.reduce((sum, line) => sum + lineSubtotal(line) * ((line.vatRate || 0) / 100), 0)
  return {
    hours: sumOf('Timer'),
    hoursCount: lines
      .filter((line) => groupOfLine(line) === 'Timer')
      .reduce((sum, line) => sum + (line.quantity || 0), 0),
    materials: sumOf('Materialer'),
    travelService: sumOf('Kørsel & service'),
    other: sumOf('Øvrigt'),
    subtotal,
    vatAmount,
    total: subtotal + vatAmount,
  }
}

const round2 = (value: number) => Math.round(value * 100) / 100

export function formatQuantity(value: number): string {
  return value.toLocaleString('da-DK', { maximumFractionDigits: 2 })
}

export function formatHours(value: number): string {
  const text = value.toLocaleString('da-DK', { minimumFractionDigits: 1, maximumFractionDigits: 2 })
  return `${text} ${value === 1 ? 'time' : 'timer'}`
}

/** Kort form til overblik: "4,5 t" */
export function formatHoursShort(value: number): string {
  return `${value.toLocaleString('da-DK', { minimumFractionDigits: 1, maximumFractionDigits: 2 })} t`
}

/** "+70 kr." / "−155 kr." / "0 kr." */
export function signedKr(value: number): string {
  const rounded = round2(value)
  if (rounded === 0) return formatKr(0)
  return `${rounded > 0 ? '+' : '−'}${formatKr(Math.abs(rounded))}`
}

/** "+1,0 t" / "−0,5 t" */
export function signedHoursShort(value: number): string {
  const rounded = round2(value)
  if (rounded === 0) return formatHoursShort(0)
  return `${rounded > 0 ? '+' : '−'}${formatHoursShort(Math.abs(rounded))}`
}

/** Over estimatet markeres diskret; under eller lig vises neutralt. */
export function diffTone(value: number): string {
  return round2(value) > 0 ? 'text-danger' : 'text-ink-soft'
}
